import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as priceApi from '../api/priceApi';
import * as materialApi from '../api/materialApi';
import * as materialGroupApi from '../api/materialGroupApi';
import * as departmentApi from '../api/departmentApi';
import * as makeApi from '../api/makeApi';
import * as priceChangeBasisApi from '../api/priceChangeBasisApi';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageHeader from '../components/common/PageHeader';
import { getCached } from '../utils/apiCache';

const fmt = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/**
 * One price row — owns its own edit-draft state locally so typing in this
 * row's New Price / Basis fields never touches PriceManagement's state and
 * never re-renders any other row. React.memo's default shallow-prop compare
 * means this only re-renders when ITS OWN `price` object reference changes
 * (an optimistic patch after a successful save) or when shared props like
 * `selected`/`canEdit`/`canDelete`/`basisOptions` actually change.
 */
const PriceRow = React.memo(function PriceRow({
  price, index, canEdit, canDelete, selected, basisOptions,
  onToggleSelect, onSaveRow, onDelete, onOpenHistory,
}) {
  const { showSnackbar } = useSnackbar();
  const [draftPrice, setDraftPrice] = useState('');
  const [draftBasisId, setDraftBasisId] = useState('');
  const [rowSaving, setRowSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const savedFlashTimer = useRef(null);

  useEffect(() => () => clearTimeout(savedFlashTimer.current), []);

  const isEdited = draftPrice !== '' && !isNaN(Number(draftPrice)) && Number(draftPrice) !== Number(price.price);
  const needsBasis = isEdited && !draftBasisId;

  const handleSave = async () => {
    if (!isEdited || rowSaving) return;
    if (needsBasis) { showSnackbar('Select a basis for this price change.', 'error'); return; }
    setRowSaving(true);
    try {
      await onSaveRow(price.id, Number(draftPrice), Number(draftBasisId));
      setDraftPrice(''); setDraftBasisId('');
      setJustSaved(true);
      clearTimeout(savedFlashTimer.current);
      savedFlashTimer.current = setTimeout(() => setJustSaved(false), 1500);
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setRowSaving(false);
    }
  };

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        bgcolor: justSaved ? '#E8F5E9 !important'
          : isEdited ? '#FFFDE7 !important'
          : selected ? '#FFF8E1 !important' : undefined,
        transition: 'background-color 0.3s',
      }}
    >
      {canDelete && <TableCell padding="checkbox"><Checkbox size="small" checked={selected} onChange={() => onToggleSelect(price.id)} /></TableCell>}
      <TableCell>{index + 1}</TableCell>
      <TableCell sx={{ fontWeight: 500, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <Tooltip title={price.material?.name}><span>{price.material?.name}</span></Tooltip>
      </TableCell>
      <TableCell><Chip label={price.material?.unit} size="small" variant="outlined" /></TableCell>
      <TableCell sx={{ color: '#1565C0' }}>{price.material?.materialGroup?.groupCode}</TableCell>
      <TableCell>{price.make?.name}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 700, color: isEdited ? '#9E9E9E' : '#2E7D32', textDecoration: isEdited ? 'line-through' : 'none' }}>
        {fmt(price.price)}
      </TableCell>
      {canEdit && (
        <TableCell align="right">
          <TextField
            size="small"
            type="number"
            value={draftPrice}
            onChange={(e) => setDraftPrice(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            placeholder={String(price.price)}
            disabled={rowSaving}
            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
            sx={{ width: 110, '& .MuiOutlinedInput-root': { bgcolor: isEdited ? '#FFF9C4' : undefined } }}
          />
        </TableCell>
      )}
      {canEdit && (
        <TableCell>
          {isEdited ? (
            <Select
              size="small"
              value={draftBasisId}
              onChange={(e) => setDraftBasisId(e.target.value)}
              displayEmpty
              disabled={rowSaving}
              sx={{ minWidth: 160 }}
              error={needsBasis}
            >
              <MenuItem value=""><em style={{ color: needsBasis ? '#d32f2f' : undefined }}>Select basis *</em></MenuItem>
              {basisOptions.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>
      )}
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {canEdit && isEdited && (
          <Tooltip title="Save this price now">
            <span>
              <IconButton size="small" color="success" onClick={handleSave} disabled={rowSaving}>
                {rowSaving ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={`Updated: ${new Date(price.updatedAt).toLocaleDateString('en-IN')}`}>
          <IconButton size="small" color="primary" onClick={() => onOpenHistory(price)}><HistoryIcon fontSize="small" /></IconButton>
        </Tooltip>
        {canDelete && (
          <Tooltip title="Delete this price">
            <IconButton size="small" color="error" onClick={() => onDelete(price)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
});

export default function PriceManagement() {
  const [departments, setDepartments] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [makes, setMakes] = useState([]);
  const [basisOptions, setBasisOptions] = useState([]);

  const [filterDept, setFilterDept] = useState(null);
  const [filterGroup, setFilterGroup] = useState(null);
  const [filterMaterial, setFilterMaterial] = useState(null);
  const [filterMake, setFilterMake] = useState(null);
  const materialSearchRef = useRef('');
  const searchTimer = useRef(null);
  const [materialInputKey, setMaterialInputKey] = useState(0);

  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Multi-select for bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Add price dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ materialId: '', makeId: '', price: '', basisId: '', remarks: '' });
  const [dialogMaterialOptions, setDialogMaterialOptions] = useState([]);
  const [dialogMaterialObj, setDialogMaterialObj] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editHistoryEntry, setEditHistoryEntry] = useState(null);
  const [editHistorySaving, setEditHistorySaving] = useState(false);

  const [, startTransition] = useTransition();

  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canEdit   = hasPermission('EDIT_PRICES');
  const canDelete = hasPermission('EDIT_PRICES') || hasPermission('DELETE_PRICES');

  useEffect(() => {
    Promise.all([
      getCached('departments:all', () => departmentApi.getAll()),
      getCached('makes:all', () => makeApi.getAll()),
      getCached('materialGroups:all', () => materialGroupApi.getAll()),
      getCached('priceChangeBasis:active', () => priceChangeBasisApi.getAll({ activeOnly: 'true' })),
    ])
      .then(([d, m, g, b]) => {
        setDepartments(Array.isArray(d) ? d : (d.data || []));
        setMakes(Array.isArray(m) ? m : (m.data || []));
        setMaterialGroups(Array.isArray(g) ? g : (g.data || []));
        setBasisOptions(Array.isArray(b) ? b : (b.data || []));
      })
      .catch(console.error);
  }, []);

  const handleGroupSelect = (group) => {
    setFilterGroup(group);
    setFilterMaterial(null);
    if (group && !filterDept) {
      const deptObj = departments.find((d) => d.id === group.departmentId);
      if (deptObj) setFilterDept(deptObj);
    }
  };

  const handleMaterialSelect = (material) => {
    setFilterMaterial(material);
    if (!material) return;
    const matGroup = material.materialGroup;
    const matDept = matGroup?.department;
    if (!filterDept && matDept) { setFilterDept(matDept); if (matGroup) setFilterGroup(matGroup); }
    else if (filterDept && !filterGroup && matGroup) setFilterGroup(matGroup);
  };

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const params = { limit: 50 };
        if (materialSearchRef.current) params.search = materialSearchRef.current;
        if (filterGroup) params.materialGroupId = filterGroup.id;
        else if (filterDept) params.departmentId = filterDept.id;
        const res = await materialApi.getAll(params);
        startTransition(() => setMaterialOptions(res.data || res));
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [filterGroup, filterDept, startTransition]);

  const loadPrices = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = {};
      if (filterMaterial) params.materialId = filterMaterial.id;
      if (filterMake) params.makeId = filterMake.id;
      const res = await priceApi.getAll(params);
      setPrices(res.data || res);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filterMaterial, filterMake, showSnackbar]);

  useEffect(() => { loadPrices(); }, [loadPrices]);

  const clearFilters = () => {
    setFilterDept(null); setFilterGroup(null);
    setFilterMaterial(null); setFilterMake(null);
    materialSearchRef.current = '';
    setMaterialInputKey((k) => k + 1);
  };

  // ── Quick-save a single row — optimistically patches only that row's price
  // in place (no full-list refetch), so saving one row never touches or
  // re-renders any other row. ────────────────────────────────────────────────
  const handleSaveRow = useCallback(async (id, price, basisId) => {
    const result = await priceApi.update(id, { price, basisId });
    setPrices((prev) => prev.map((p) => (
      p.id === id ? { ...p, price: Number(price), updatedAt: result?.updatedAt || new Date().toISOString() } : p
    )));
    showSnackbar('Price updated. History recorded.', 'success');
  }, [showSnackbar]);

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleRow = useCallback((id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const toggleAll = () => selectedIds.size === prices.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(prices.map((p) => p.id)));
  const allSelected  = prices.length > 0 && selectedIds.size === prices.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < prices.length;

  // ── Add price dialog ──────────────────────────────────────────────────────
  const loadDialogMaterials = async (search = '') => {
    try {
      const res = await materialApi.getAll({ limit: 60, ...(search ? { search } : {}) });
      setDialogMaterialOptions(res.data || res);
    } catch (e) { console.error(e); }
  };

  const openCreate = () => {
    setDialogMaterialObj(null);
    setForm({ materialId: '', makeId: '', price: '', basisId: '', remarks: '' });
    loadDialogMaterials();
    setDialogOpen(true);
  };

  const handleAddSave = async () => {
    setSaving(true);
    try {
      await priceApi.create({ materialId: Number(form.materialId), makeId: Number(form.makeId), price: Number(form.price), basisId: form.basisId ? Number(form.basisId) : undefined, remarks: form.remarks });
      showSnackbar('Price added. History recorded.', 'success');
      setDialogOpen(false); loadPrices();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    try { await priceApi.remove(deleteTarget.id); showSnackbar('Removed from Price Management. History preserved.', 'info'); setDeleteTarget(null); loadPrices(); }
    catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  }, [deleteTarget, showSnackbar, loadPrices]);

  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      await priceApi.batchRemove([...selectedIds]);
      showSnackbar(`${selectedIds.size} item(s) removed from Price Management. History preserved.`, 'info');
      loadPrices();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  // ── History ───────────────────────────────────────────────────────────────
  const openHistory = useCallback(async (row) => {
    setHistoryItem(row); setHistoryLoading(true);
    try { const res = await priceApi.getHistory({ materialId: row.materialId, makeId: row.makeId }); setHistory(res.data || res); }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setHistoryLoading(false); }
  }, [showSnackbar]);

  const openEditHistoryEntry = (h) => setEditHistoryEntry({ id: h.id, basisId: h.basis?.id || '', remarks: h.remarks || '' });

  const handleSaveHistoryBasis = async () => {
    setEditHistorySaving(true);
    try {
      await priceApi.updateHistoryBasis(editHistoryEntry.id, {
        basisId: editHistoryEntry.basisId ? Number(editHistoryEntry.basisId) : null,
        remarks: editHistoryEntry.remarks || null,
      });
      showSnackbar('Price history updated.', 'success');
      setEditHistoryEntry(null);
      if (historyItem) {
        const res = await priceApi.getHistory({ materialId: historyItem.materialId, makeId: historyItem.makeId });
        setHistory(res.data || res);
      }
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setEditHistorySaving(false); }
  };

  const hasFilters = filterDept || filterGroup || filterMaterial || filterMake;
  const filteredGroups = useMemo(
    () => (filterDept ? materialGroups.filter((g) => g.departmentId === filterDept.id) : materialGroups),
    [materialGroups, filterDept]
  );
  const existingCombo = useMemo(
    () => (form.materialId && form.makeId
      ? prices.find((p) => p.materialId === Number(form.materialId) && p.makeId === Number(form.makeId))
      : null),
    [form.materialId, form.makeId, prices]
  );

  return (
    <Box>
      <PageHeader
        title="Price Management"
        subtitle="Edit prices inline — type a new price, pick a basis, then click the ✔ to save that row instantly."
        action={canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Price</Button>}
      />
      {!canEdit && !canDelete && <Alert severity="info" sx={{ mb: 2 }}>You have read-only access to prices.</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterListIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>Filters</Typography>
            {hasFilters && <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ ml: 'auto' }}>Clear All</Button>}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={departments} getOptionLabel={(o) => o.name}
                value={filterDept} onChange={(_, v) => { setFilterDept(v); setFilterGroup(null); setFilterMaterial(null); }}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Department" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={filteredGroups} getOptionLabel={(o) => o.groupCode}
                value={filterGroup} onChange={(_, v) => handleGroupSelect(v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Group Code" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{o.groupCode}</Typography>
                      {!filterDept && <Typography variant="caption" color="text.secondary">{departments.find((d) => d.id === o.departmentId)?.name || ''}</Typography>}
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                key={materialInputKey}
                size="small"
                options={materialOptions}
                getOptionLabel={(o) => o.name}
                value={filterMaterial}
                onChange={(_, v) => handleMaterialSelect(v)}
                onInputChange={(_, v) => {
                  materialSearchRef.current = v;
                  clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(async () => {
                    try {
                      const params = { limit: 50 };
                      if (v) params.search = v;
                      if (filterGroup) params.materialGroupId = filterGroup.id;
                      else if (filterDept) params.departmentId = filterDept.id;
                      const res = await materialApi.getAll(params);
                      startTransition(() => setMaterialOptions(res.data || res));
                    } catch (e) { console.error(e); }
                  }, 300);
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Material" helperText="Type to search" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box><Typography variant="body2" fontWeight={500}>{o.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.unit} · {o.materialGroup?.groupCode}</Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={makes} getOptionLabel={(o) => o.name}
                value={filterMake} onChange={(_, v) => setFilterMake(v)} isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Make" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk delete toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} row(s) selected</Typography>
          <Button variant="contained" color="error" size="small" startIcon={<DeleteSweepIcon />} onClick={() => setBulkDeleteOpen(true)}>Delete Selected</Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>Clear Selection</Button>
        </Box>
      )}

      <Card>
        {loading ? <LoadingSpinner /> : (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                  {canDelete && <TableCell padding="checkbox"><Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={toggleAll} /></TableCell>}
                  <TableCell>#</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Make</TableCell>
                  <TableCell align="right">Current Price</TableCell>
                  {canEdit && <TableCell align="right" sx={{ color: '#1565C0', fontWeight: 700 }}>New Price</TableCell>}
                  {canEdit && <TableCell sx={{ minWidth: 170, color: '#1565C0', fontWeight: 700 }}>Basis for Change</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prices.map((p, i) => (
                  <PriceRow
                    key={p.id}
                    price={p}
                    index={i}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    selected={selectedIds.has(p.id)}
                    basisOptions={basisOptions}
                    onToggleSelect={toggleRow}
                    onSaveRow={handleSaveRow}
                    onDelete={setDeleteTarget}
                    onOpenHistory={openHistory}
                  />
                ))}
                {prices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? (canDelete ? 10 : 9) : (canDelete ? 8 : 7)} align="center" sx={{ py: 4, color: '#9e9e9e' }}>
                      {hasFilters ? 'No prices found for selected filters' : 'No prices found. Use "Add Price" to add one.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Footer */}
        {prices.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {prices.length} record(s){canEdit ? ' — edit a price and click ✔ to save it instantly' : ''}
            </Typography>
            {canDelete && selectedIds.size > 0 && <Typography variant="caption" color="text.secondary">{selectedIds.size} selected</Typography>}
          </Box>
        )}
      </Card>

      {/* Add Price Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Price</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Autocomplete
            options={dialogMaterialOptions}
            getOptionLabel={(o) => o.name || ''}
            value={dialogMaterialObj}
            onChange={(_, v) => { setDialogMaterialObj(v); setForm((f) => ({ ...f, materialId: v ? v.id : '' })); }}
            onInputChange={(_, v, reason) => { if (reason === 'input') loadDialogMaterials(v); }}
            filterOptions={(x) => x}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Material *" helperText="Type to search" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.unit} · {o.materialGroup?.groupCode}</Typography>
                </Box>
              </li>
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Make *</InputLabel>
            <Select value={form.makeId} label="Make *" onChange={(e) => setForm({ ...form, makeId: e.target.value })}>
              {makes.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </Select>
          </FormControl>
          {existingCombo && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Already has a price of <strong>{fmt(existingCombo.price)}</strong>. Saving will update it.
            </Alert>
          )}
          <TextField label="Price (₹)" type="number" fullWidth value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <FormControl fullWidth>
            <InputLabel>Basis for Change (optional)</InputLabel>
            <Select value={form.basisId} label="Basis for Change (optional)" onChange={(e) => setForm({ ...form, basisId: e.target.value })}>
              <MenuItem value=""><em>— None / Not specified —</em></MenuItem>
              {basisOptions.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Remarks (optional)" fullWidth multiline rows={2} value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            placeholder="Any additional notes about this price..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSave} variant="contained" disabled={!form.price || !form.materialId || !form.makeId || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyItem} onClose={() => setHistoryItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>Price History — {historyItem?.material?.name} / {historyItem?.make?.name}</DialogTitle>
        <DialogContent>
          {historyLoading ? <LoadingSpinner /> : (
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell><TableCell>Price</TableCell><TableCell>Basis</TableCell>
                  <TableCell>Updated By</TableCell><TableCell>Remarks</TableCell><TableCell>Recorded At</TableCell>
                  {canEdit && <TableCell sx={{ width: 44 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={h.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2E7D32' }}>{fmt(h.price)}</TableCell>
                    <TableCell>
                      {h.basis ? <Chip label={h.basis.name} size="small" color="info" /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>
                      {h.user ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LockIcon sx={{ fontSize: 12, color: '#757575' }} />
                          <Typography variant="body2">{h.user.username}</Typography>
                        </Box>
                      ) : <Typography variant="caption" color="text.disabled">System</Typography>}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}><Typography variant="caption">{h.remarks || '—'}</Typography></TableCell>
                    <TableCell>{new Date(h.recordedAt).toLocaleString('en-IN')}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <Tooltip title={h.basis ? 'Edit basis / remarks' : 'Set missing basis'}>
                          <IconButton size="small" color={h.basis ? 'default' : 'warning'} onClick={() => openEditHistoryEntry(h)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {history.length === 0 && <TableRow><TableCell colSpan={canEdit ? 7 : 6} align="center">No history</TableCell></TableRow>}
              </TableBody>
            </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setHistoryItem(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Edit history entry */}
      <Dialog open={!!editHistoryEntry} onClose={() => setEditHistoryEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Price History Entry</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Basis of Price Change</InputLabel>
            <Select value={editHistoryEntry?.basisId || ''} label="Basis of Price Change"
              onChange={(e) => setEditHistoryEntry((prev) => ({ ...prev, basisId: e.target.value }))}>
              <MenuItem value=""><em>— None / Not specified —</em></MenuItem>
              {basisOptions.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Remarks" fullWidth multiline rows={2} value={editHistoryEntry?.remarks || ''}
            onChange={(e) => setEditHistoryEntry((prev) => ({ ...prev, remarks: e.target.value }))}
            placeholder="Optional notes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditHistoryEntry(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveHistoryBasis} disabled={editHistorySaving}>
            {editHistorySaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove from Price Management"
        message="This item will be removed from Price Management. Its complete price history will be preserved and will remain available for reporting and audit purposes."
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Bulk delete confirm */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Remove ${selectedIds.size} Item(s) from Price Management`}
        message="These items will be removed from Price Management. Their complete price history will be preserved and will remain available for reporting and audit purposes."
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
        confirmColor="error"
      />
    </Box>
  );
}
